import { resolveProfileAutotest } from './harness';
import { workFacts } from './pageData';
import { viewedUserId } from './profileFacts';
import {
  injectArmStyles,
  mountSlot,
  observeAppendedCards,
  readCards,
  reorderCards,
  setSurfaceFlag,
} from './surface';
import { trackArmClick } from './tracking';

import type { ArmContext, ArmSpec, ArmStamp } from './armRegistry';

function resolveArmStamp(spec: ArmSpec, ctx: ArmContext): ArmStamp | undefined {
  try {
    return spec.stamp?.(ctx);
  } catch {
    return undefined;
  }
}

// A hook may be async, and a synchronous try only catches what one does before
// its first await: everything after that settles on the returned promise
// instead, where an unhandled rejection is a page-level error event nobody
// wired. So every hook call is wrapped in a try AND has its result contained.
//
// Nothing here awaits, which is why an onReady that fails asynchronously does
// not gate the cards pass the way a synchronous throw does — waiting on it
// would hold the first render behind whatever that arm went to fetch.
function contain(result: void | Promise<void>): void {
  void Promise.resolve(result).catch(() => {});
}

export function setUpProfileAutotest(): void {
  try {
    const resolved = resolveProfileAutotest();
    if (resolved == null || resolved.spec == null) return;

    const userId = viewedUserId();
    if (userId == null) return;

    // The arm's whole CSS scope, resolved before ctx because ctx carries it.
    // Empty means setSurfaceFlag found no shell AND no <body> to fall back to,
    // which is not a thinner profile page but no page at all — and an arm run
    // with it would compose every recipe as ' .page-class', a valid selector
    // that matches document-wide. Control is the right render for that.
    const scope = setSurfaceFlag('arm', { value: resolved.bucket }) ?? '';
    if (scope === '') return;

    const spec = resolved.spec;
    let armStamp: ArmStamp | undefined = undefined;

    const ctx: ArmContext = {
      viewedUserId: userId,
      scope,
      // Unwrapped: every slot anchor is inside the shell the stamp landed on,
      // so a mounted slot is already within ctx.scope and needs none of its own.
      mountSlot,
      cards: readCards,
      reorderCards,
      setSurfaceFlag,
      injectStyles: (css) => injectArmStyles(resolved.bucket, css),
      workFacts: () => workFacts(userId),
      track: (source, options) =>
        trackArmClick(source, { ...resolved.stamp, ...armStamp }, options),
    };

    // Resolved once, before any render hook, so render_state describes what the
    // arm predicted rather than what it happens to see at click time. Declared
    // above ctx rather than here: stamp is documented as reads-only, but an arm
    // that tracks from it would otherwise hit the TDZ and lose its whole
    // render_state column to the catch below.
    armStamp = resolveArmStamp(spec, ctx);

    const onCards = spec.onCards;

    // Contained apart from the observer install below so that a throw here
    // cannot skip it. An arm that hid the section headings and then threw would
    // otherwise leave them hidden with nothing left to hide the ones the
    // paginator appends, and every later batch grows a visible heading mid-list.
    try {
      contain(spec.onReady?.(ctx));
      contain(onCards?.(readCards(), ctx));
    } catch {
      // Whatever the arm applied before throwing stands; it cannot be undone.
    }

    // Installed for every arm, not just the ones with onCards, and whether or
    // not the pass above threw: it is also what keeps hidden section headings
    // hidden across pagination. The callback fires on a fresh stack long after
    // this frame returns, so it needs its own containment - without it one bad
    // card throws on every scroll batch after.
    observeAppendedCards((appended) => {
      try {
        contain(onCards?.(appended, ctx));
      } catch {
        // One bad batch must not break the rest of the page.
      }
    });
  } catch {
    // The arm's own render pass is contained above, so what is left here is
    // resolution and install. The one thing this cannot undo is the surface
    // flag: if observeAppendedCards is what threw, a pure-CSS arm keyed off
    // data-liautotest-arm is already applied and now has no re-hide on append.
  }
}
